import React, { useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import config from "../../../config";
import { Grid, Typography } from "@mui/material";
import { trackPage } from "../../../analytics";
import colors from "../../../assets/colors";
import { SignupCard, CtaButton, StepHeader } from "../../../signupComponents";
import HomeWorkIcon from "@material-ui/icons/HomeWork";
import TerrainIcon from "@material-ui/icons/Terrain";

// 👇 from MUI v4
import withStyles from "@material-ui/core/styles/withStyles";

const styles = theme => ({
  heroContainer: {
    maxWidth: 820,
    width: "92%",
    textAlign: "center",
    marginTop: theme.spacing(2)
  },
  heroTitle: {
    fontFamily: '"Ubuntu","Helvetica","Arial",sans-serif !important',
    fontWeight: 800,
    fontSize: 28,
    lineHeight: 1.22,
    color: colors.mcsdark,
    textAlign: "center",
    marginBottom: theme.spacing(1),
    "@media (max-width: 600px)": {
      fontSize: 24
    }
  },
  heroSubtitle: {
    fontFamily: '"Ubuntu","Helvetica","Arial",sans-serif !important',
    fontWeight: 600,
    fontSize: 17,
    textAlign: "center",
    color: colors.mcsdark,
    marginBottom: 0,
    "@media (max-width: 600px)": {
      fontSize: 15
    }
  },
  cardIntro: {
    fontFamily: '"Ubuntu","Helvetica","Arial",sans-serif !important',
    color: "#526a7a",
    fontWeight: 600,
    fontSize: 15,
    textAlign: "center",
    margin: "4px 0 22px"
  },
  choiceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    "@media (max-width: 700px)": {
      gridTemplateColumns: "1fr"
    }
  },
  choiceCard: {
    position: "relative",
    minHeight: 180,
    border: "2px solid rgba(9, 64, 102, 0.16)",
    borderRadius: 14,
    background: "#f8fbfd",
    padding: theme.spacing(2.5),
    textAlign: "left",
    cursor: "pointer",
    transition:
      "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, background-color 160ms ease",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    "&:hover": {
      borderColor: "rgba(221, 52, 73, 0.55)",
      backgroundColor: "#fff",
      transform: "translateY(-1px)",
      boxShadow: "0 10px 24px rgba(9, 64, 102, 0.10)"
    },
    "&:focus": {
      outline: "3px solid rgba(39, 170, 225, 0.35)",
      outlineOffset: 2
    }
  },
  choiceCardSelected: {
    borderColor: colors.mcsred,
    backgroundColor: "#fff",
    boxShadow: "0 12px 26px rgba(221, 52, 73, 0.16)"
  },
  choiceIcon: {
    width: 46,
    height: 46,
    borderRadius: 12,
    backgroundColor: "rgba(39, 170, 225, 0.13)",
    color: colors.mcsdark,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(2)
  },
  choiceTitle: {
    color: colors.mcsdark,
    fontWeight: 800,
    fontSize: 22,
    lineHeight: 1.2,
    margin: "0 0 8px"
  },
  choiceText: {
    color: "#526a7a",
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 1.45,
    margin: 0
  },
  selectedBadge: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: `5px solid ${colors.mcsred}`,
    background: "#fff"
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    "@media (max-width: 600px)": {
      "& button": {
        width: "100%"
      }
    }
  }
});

const locationOptions = [
  {
    value: "inside",
    title: "Inside a community",
    text: "Town, city, village, hamlet, summer village, lake, or subdivision.",
    Icon: HomeWorkIcon
  },
  {
    value: "outside",
    title: "Rural or outside",
    text: "Acreage, farm, rural property, municipal address, or land location.",
    Icon: TerrainIcon
  }
];

const Home = props => {
  const { classes } = props;
  const [choice, setChoice] = useState("");

  useEffect(() => {
    trackPage("home");
  }, []);

  const goNext = () => {
    if (!choice) return;

    const path =
      choice === "inside"
        ? config.SIGNUP.APP_PATH + "/s/autolocation"
        : config.SIGNUP.APP_PATH + "/s/outside";

    props.setSignupclass(choice);

    props.history.push(path);
  };

  return (
    <Grid container justifyContent="center" sx={{ width: "100%" }}>
      <Grid
        item
        xs={12}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 2, md: 3 }
        }}
      >
        {/* Intro */}
        <Grid item className={classes.heroContainer}>
          <Typography component="h1" className={classes.heroTitle}>
            Check Internet availability for your location
          </Typography>

          <Typography className={classes.heroSubtitle}>
            Start by choosing which kind of location needs service.
          </Typography>
        </Grid>

        {/* Card */}
        <Grid
          item
          sx={{
            width: "min(1040px, calc(100vw - 32px))",
            maxWidth: "100%",
            display: "flex",
            justifyContent: "center"
          }}
        >
          <SignupCard size="xl" maxw={1040}>
            <StepHeader
              step={1}
              eyebrow="Service location"
              title="Which best describes where you want Internet?"
            />

            <div className={classes.choiceGrid}>
              {locationOptions.map(({ value, title, text, Icon }) => {
                const selected = choice === value;
                return (
                  <div
                    key={value}
                    role="button"
                    tabIndex={0}
                    aria-pressed={selected}
                    className={
                      classes.choiceCard +
                      (selected ? " " + classes.choiceCardSelected : "")
                    }
                    onClick={() => setChoice(value)}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setChoice(value);
                      }
                    }}
                  >
                    {selected ? (
                      <span className={classes.selectedBadge} />
                    ) : null}
                    <div>
                      <div className={classes.choiceIcon}>
                        <Icon />
                      </div>
                      <h3 className={classes.choiceTitle}>{title}</h3>
                      <p className={classes.choiceText}>{text}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={classes.actionRow}>
              <CtaButton
                variant="contained"
                disabled={!choice}
                onClick={goNext}
              >
                NEXT STEP
              </CtaButton>
            </div>
          </SignupCard>
        </Grid>
      </Grid>
    </Grid>
  );
};

// 👇 wrap with withStyles AND withRouter
export default withStyles(styles)(withRouter(Home));
